import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiList, FiClock, FiShield, FiTrendingUp, FiZap, FiInfo } = FiIcons;

const FundCategoryReferenceTable = () => {
  const data = [
    { 
      horizon: '< 3 Years', 
      icon: FiShield,
      classes: {
        rowBg: 'bg-red-50/50',
        hoverBg: 'hover:bg-red-50',
        border: 'border-red-100',
        headerCell: 'bg-red-50 text-red-900',
        text: 'text-red-800',
        iconColor: 'text-red-600'
      },
      conservative: 'Overnight funds, liquid funds, ultra short term funds', 
      moderate: 'Liquid funds, ultra short term funds, low duration funds', 
      aggressive: 'Ultra short term funds, Arbitrage Funds, Equity Savings, Credit Risk Funds' 
    },
    { 
      horizon: '3 - 5 Years', 
      icon: FiClock,
      classes: {
        rowBg: 'bg-orange-50/50',
        hoverBg: 'hover:bg-orange-50',
        border: 'border-orange-100',
        headerCell: 'bg-orange-50 text-orange-900',
        text: 'text-orange-800',
        iconColor: 'text-orange-600'
      },
      conservative: 'Dynamic Asset allocation, Conservative hybrid', 
      moderate: 'Balanced Hybrid, Aggressive Hybrid', 
      aggressive: 'Aggressive Hybrid, Large cap funds' 
    },
    { 
      horizon: '5 - 10 Years', 
      icon: FiTrendingUp,
      classes: {
        rowBg: 'bg-blue-50/50',
        hoverBg: 'hover:bg-blue-50',
        border: 'border-blue-100',
        headerCell: 'bg-blue-50 text-blue-900',
        text: 'text-blue-800',
        iconColor: 'text-blue-600'
      },
      conservative: 'Large cap, Flexi cap', 
      moderate: 'Large & Midcap, Flexi cap funds', 
      aggressive: 'Focused, Flexi cap funds, Large & Mid cap Funds' 
    },
    { 
      horizon: '10+ Years', 
      icon: FiZap,
      classes: {
        rowBg: 'bg-green-50/50',
        hoverBg: 'hover:bg-green-50',
        border: 'border-green-100',
        headerCell: 'bg-green-50 text-green-900',
        text: 'text-green-800',
        iconColor: 'text-green-600'
      },
      conservative: 'Focused, Large & Mid Cap', 
      moderate: 'Large & Mid Cap, Mid cap', 
      aggressive: 'Mid Cap, Small cap Funds' 
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.5 }}
      className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden mt-12 break-inside-avoid"
    >
      <div className="bg-gradient-to-r from-gray-50 to-white p-6 border-b border-gray-100">
        <div className="flex items-center space-x-3">
          <SafeIcon icon={FiList} className="text-2xl text-indigo-600" />
          <div>
            <h2 className="text-xl font-bold text-gray-900">Fund Category Reference Guide</h2>
            <p className="text-sm text-gray-500 mt-1">Recommended fund categories based on investment horizon and risk profile</p>
          </div>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left border-collapse">
          <thead>
            <tr className="bg-gray-50 text-gray-700 font-semibold border-b border-gray-200 shadow-sm">
              <th className="px-6 py-4 min-w-[180px] border-r border-gray-200">Investment Horizon</th>
              <th className="px-6 py-4 min-w-[200px] border-r border-gray-200">Conservative</th>
              <th className="px-6 py-4 min-w-[200px] border-r border-gray-200">Moderate</th>
              <th className="px-6 py-4 min-w-[200px]">Aggressive</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.map((row, index) => (
              <tr key={index} className={`${row.classes.rowBg} ${row.classes.hoverBg} transition-colors border-b ${row.classes.border}`}>
                <td className={`px-6 py-5 font-bold border-r ${row.classes.border} ${row.classes.headerCell}`}>
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={row.icon} className={`text-lg ${row.classes.iconColor}`} />
                    <span>{row.horizon}</span>
                  </div>
                </td>
                <td className={`px-6 py-5 ${row.classes.text} border-r ${row.classes.border} align-middle`}>
                  {row.conservative}
                </td>
                <td className={`px-6 py-5 ${row.classes.text} border-r ${row.classes.border} align-middle`}>
                  {row.moderate}
                </td>
                <td className={`px-6 py-5 ${row.classes.text} align-middle`}>
                  {row.aggressive}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      <div className="bg-gray-50 p-4 text-xs text-gray-500 border-t border-gray-200 flex items-start space-x-2">
        <SafeIcon icon={FiInfo} className="mt-0.5 text-gray-400 flex-shrink-0" />
        <p>Note: This table is for general reference only. Actual fund selection should be based on your personal financial situation and advice from a SEBI-registered investment advisor.</p>
      </div>
    </motion.div>
  );
};

export default FundCategoryReferenceTable;